/**
 * Selector de Excel Contalink: archivo suelto o carpeta con varios .xls/.xlsx.
 * El input de carpeta XML (webkitdirectory) es independiente.
 *
 * Importante: en el preview del IDE / webviews, showOpenFilePicker y
 * showDirectoryPicker suelen “seleccionar” sin devolver archivos al JS.
 * Por eso el camino por defecto es el <input type="file"> clásico
 * (archivo) y webkitdirectory (carpeta); el File System Access API es opt-in.
 */

export const EXCEL_FILE_ACCEPT =
  ".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export const EXCEL_NO_FILES_IN_FOLDER_MSG =
  "La carpeta no contiene archivos Excel (.xls / .xlsx). " +
  "Seleccione la carpeta donde guardó los reportes Contalink / Control IVA.";

export const EXCEL_INVALID_FILE_MSG =
  "El archivo no es un Excel válido (.xls / .xlsx). Vuelva a intentar con el reporte Contalink o la plantilla.";

export const EXCEL_FOLDER_EMPTY_MSG =
  "No se recibieron archivos de la carpeta. Vuelva a pulsar «Cargar carpeta Excel» e intente de nuevo.";

export const EXCEL_PICK_FILE_MSG =
  "Seleccione el archivo Excel (.xls / .xlsx) en el diálogo del sistema…";

export const EXCEL_PICK_FOLDER_MSG =
  "Seleccione la carpeta con los reportes Contalink en el diálogo del sistema…";

const EXCEL_OPEN_PICKER_TYPES = [
  {
    description: "Excel",
    accept: {
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    },
  },
];

/** Extensión .xls / .xlsx / .xlsm (case-insensitive). */
export function isExcelFileName(name) {
  const n = String(name || "").toLowerCase().trim();
  return n.endsWith(".xls") || n.endsWith(".xlsx") || n.endsWith(".xlsm");
}

/**
 * Infiere upload_intent Contalink desde el nombre del archivo (carpeta multi-archivo).
 * Si no hay pista clara, devuelve `fallback` (opción elegida en la UI).
 * @param {File|{name?: string, webkitRelativePath?: string}|null|undefined} file
 * @param {string} [fallback='ingreso']
 * @returns {'ingreso'|'egreso'|'nomina'|string}
 */
export function inferExcelUploadIntentFromFile(file, fallback = "ingreso") {
  const name = String(file?.webkitRelativePath || file?.name || "").toLowerCase();
  // Nómina pura (sin "gasto" en el nombre). Archivos tipo gastosy_nomina → egreso
  // (el backend separa gastos y nóminas en lotes distintos al persistir).
  const looksPureNomina =
    /n[oó]mina/.test(name)
    && !/gasto|egreso/.test(name);
  const looksEgreso = /egreso|gasto/.test(name);
  const looksIngreso = /ingreso|venta|cobro/.test(name);
  if (looksPureNomina && !looksIngreso) return "nomina";
  if (looksIngreso && !looksEgreso) return "ingreso";
  if (looksEgreso && !looksIngreso) return "egreso";
  return fallback || "ingreso";
}

/** Nombre o MIME típico de Excel (incluye octet-stream con extensión Excel). */
export function isExcelFile(file) {
  if (!file) return false;
  if (isExcelFileName(file.name)) return true;
  const t = String(file.type || "").toLowerCase();
  if (!t) return false;
  return (
    t.includes("spreadsheet")
    || t.includes("excel")
    || t === "application/vnd.ms-excel"
    || t === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  );
}

/**
 * Extrae archivos Excel de una FileList (archivo suelto, multi o carpeta webkitdirectory).
 * @param {FileList|File[]|null|undefined} fileList
 * @returns {File[]}
 */
export function collectExcelFiles(fileList) {
  const files = Array.from(fileList || []);
  return files.filter((f) => f && isExcelFile(f));
}

/**
 * @param {FileList|File[]|null|undefined} fileList
 * @returns {{ ok: true, files: File[] } | { ok: false, reason: 'empty'|'no_excel' }}
 */
export function resolveExcelFiles(fileList) {
  const all = Array.from(fileList || []);
  if (!all.length) return { ok: false, reason: "empty" };
  const files = collectExcelFiles(all);
  if (!files.length) return { ok: false, reason: "no_excel" };
  files.sort((a, b) => {
    const pa = String(a.webkitRelativePath || a.name || "");
    const pb = String(b.webkitRelativePath || b.name || "");
    return pa.localeCompare(pb, "es");
  });
  return { ok: true, files };
}

/** @deprecated use resolveExcelFiles */
export function resolveExcelSelection(fileList) {
  const r = resolveExcelFiles(fileList);
  if (!r.ok) {
    return { ok: false, reason: r.reason === "no_excel" ? "invalid" : r.reason };
  }
  return { ok: true, file: r.files[0], files: r.files };
}

/** Quita atributos de carpeta (input de un solo archivo). */
export function ensureExcelFileInput(el) {
  if (!el) return null;
  el.removeAttribute("webkitdirectory");
  el.removeAttribute("directory");
  el.removeAttribute("mozdirectory");
  try {
    el.webkitdirectory = false;
  } catch {
    /* ignore */
  }
  if ("directory" in el) {
    try {
      el.directory = false;
    } catch {
      /* ignore */
    }
  }
  if (!el.getAttribute("accept") || !String(el.getAttribute("accept")).includes(".xls")) {
    el.setAttribute("accept", EXCEL_FILE_ACCEPT);
  }
  el.removeAttribute("multiple");
  return el;
}

/**
 * Activa selector de carpeta. Preferir también atributos en JSX
 * (`webkitdirectory=""` / `directory=""`) como en carga XML.
 */
export function ensureExcelFolderInput(el) {
  if (!el) return null;
  el.setAttribute("webkitdirectory", "");
  el.setAttribute("directory", "");
  el.setAttribute("mozdirectory", "");
  try {
    el.webkitdirectory = true;
  } catch {
    /* ignore */
  }
  el.multiple = true;
  el.removeAttribute("accept");
  return el;
}

/**
 * Recorre un DirectoryHandle y junta Excel + nombres vistos (para diagnóstico).
 * @param {FileSystemDirectoryHandle} dirHandle
 * @param {string} [prefix]
 * @returns {Promise<{ files: File[], scannedNames: string[] }>}
 */
export async function collectExcelFromDirectoryHandle(dirHandle, prefix = "") {
  const out = [];
  const scannedNames = [];
  if (!dirHandle || typeof dirHandle.values !== "function") {
    return { files: out, scannedNames };
  }
  for await (const entry of dirHandle.values()) {
    const path = prefix ? `${prefix}/${entry.name}` : entry.name;
    if (entry.kind === "file") {
      scannedNames.push(path);
      if (!isExcelFileName(entry.name)) continue;
      try {
        const file = await entry.getFile();
        if (file) {
          try {
            Object.defineProperty(file, "webkitRelativePath", {
              value: path,
              configurable: true,
            });
          } catch {
            /* ignore */
          }
          out.push(file);
        }
      } catch {
        /* skip unreadable */
      }
    } else if (entry.kind === "directory") {
      const nested = await collectExcelFromDirectoryHandle(entry, path);
      out.push(...nested.files);
      scannedNames.push(...nested.scannedNames);
    }
  }
  return { files: out, scannedNames };
}

/**
 * Abre el diálogo de un archivo Excel.
 * Por defecto usa <input type="file"> (fiable en preview IDE y Chrome).
 * El archivo llega por onChange del input; este método suele devolver null.
 *
 * @param {HTMLInputElement|null} inputEl
 * @param {{ preferFileSystemAccess?: boolean }} [options]
 * @returns {Promise<File|null>}
 */
export async function pickExcelFile(inputEl, options = {}) {
  ensureExcelFileInput(inputEl);
  const preferFs = Boolean(options.preferFileSystemAccess);

  if (
    preferFs
    && typeof window !== "undefined"
    && typeof window.showOpenFilePicker === "function"
  ) {
    try {
      const handles = await window.showOpenFilePicker({
        multiple: false,
        excludeAcceptAllOption: false,
        types: EXCEL_OPEN_PICKER_TYPES,
      });
      const handle = handles?.[0];
      if (!handle) return null;
      const file = await handle.getFile();
      if (!file) return null;
      if (!isExcelFile(file)) return null;
      return file;
    } catch (err) {
      if (err && (err.name === "AbortError" || err.name === "NotAllowedError")) {
        return null;
      }
      // Caer al input clásico.
    }
  }

  if (inputEl && typeof inputEl.click === "function") {
    inputEl.value = "";
    inputEl.click();
  }
  return null;
}

/**
 * Elige carpeta. Por defecto usa <input webkitdirectory> (deferred → onChange).
 * showDirectoryPicker solo si options.preferDirectoryPicker === true.
 *
 * @returns {Promise<{
 *   status: 'ok'|'empty'|'cancelled'|'deferred'|'error',
 *   files?: File[],
 *   message?: string,
 *   scannedNames?: string[],
 * }>}
 */
export async function pickExcelFilesFromFolder(inputEl, options = {}) {
  const preferDir = Boolean(options.preferDirectoryPicker);

  if (
    preferDir
    && typeof window !== "undefined"
    && typeof window.showDirectoryPicker === "function"
  ) {
    try {
      const dir = await window.showDirectoryPicker({ mode: "read" });
      const { files, scannedNames } = await collectExcelFromDirectoryHandle(dir);
      files.sort((a, b) => {
        const pa = String(a.webkitRelativePath || a.name || "");
        const pb = String(b.webkitRelativePath || b.name || "");
        return pa.localeCompare(pb, "es");
      });
      if (!files.length) {
        const sample = scannedNames.slice(0, 8).join(", ");
        return {
          status: "empty",
          files: [],
          scannedNames,
          message: sample
            ? `${EXCEL_NO_FILES_IN_FOLDER_MSG} Archivos vistos: ${sample}${scannedNames.length > 8 ? "…" : ""}`
            : EXCEL_NO_FILES_IN_FOLDER_MSG,
        };
      }
      return { status: "ok", files, scannedNames };
    } catch (err) {
      if (err && (err.name === "AbortError" || err.name === "NotAllowedError")) {
        return { status: "cancelled", message: "Selección de carpeta cancelada." };
      }
      return {
        status: "error",
        message:
          `No se pudo leer la carpeta (${err?.message || err}). `
          + "Use «Cargar carpeta Excel» de nuevo o cargue archivo por archivo.",
      };
    }
  }

  ensureExcelFolderInput(inputEl);
  if (!inputEl || typeof inputEl.click !== "function") {
    return {
      status: "error",
      message: "Este navegador no permite elegir carpeta. Use «Cargar archivo Excel».",
    };
  }
  try {
    inputEl.value = "";
  } catch {
    /* ignore */
  }
  inputEl.click();
  return {
    status: "deferred",
    message: EXCEL_PICK_FOLDER_MSG,
  };
}

/** @deprecated use pickExcelFilesFromFolder */
export function pickExcelFolder(inputEl) {
  ensureExcelFolderInput(inputEl);
  if (inputEl && typeof inputEl.click === "function") {
    inputEl.click();
  }
}
